import { useMemo, useRef, useState } from 'react';
import { normalizeAr } from '@matix/utils';
import { fmt } from '../../lib/mock-data';
import { useLang } from '../../lib/language';
import { useLocalizedName } from '../../lib/localizedName';
import type { CatalogItemModifierGroup } from '@matix/types';

/** option id → quantity typed (only options with a quantity are present). */
export type QtyMap = Record<number, number>;

const STEP = 0.25;
const round3 = (n: number): number => Math.round(n * 1000) / 1000;

/**
 * A "choices with quantity" modifier group (admin: نوع الإضافة → اختيارات
 * بكمية). The cashier searches the options and types a quantity for each —
 * 3.5 m of a fabric — choosing as many options as the group's Max allows.
 * The option's price is per unit, and its linked item is deducted × the
 * quantity. Ordinary groups never reach this component.
 *
 * Keyboard flow, as in the spec mock-up: Enter in the search jumps to the
 * first result's quantity; Enter in a quantity returns to the search for
 * the next option.
 */
export function QuantityModifierGroup({
  group, qtys, onChange,
}: {
  group: CatalogItemModifierGroup;
  qtys: QtyMap;
  onChange: (next: QtyMap) => void;
}) {
  const { t } = useLang();
  const localized = useLocalizedName();
  const [q, setQ] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const options = group.options.filter((o) => o.is_active);
  const chosenIds = options.filter((o) => (qtys[o.id] ?? 0) > 0).map((o) => o.id);
  const atMax = chosenIds.length >= group.max_selections;

  const rows = useMemo(() => {
    const terms = normalizeAr(q).split(/\s+/).filter(Boolean);
    return options.filter((o) => {
      if (onlySelected && !((qtys[o.id] ?? 0) > 0)) return false;
      if (terms.length === 0) return true;
      const hay = normalizeAr(`${o.name ?? ''} ${o.name_ar ?? ''}`);
      return terms.every((term) => hay.includes(term));
    });
    // options is derived from the group on every render; the group is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, q, onlySelected, qtys]);

  function setQty(id: number, value: number) {
    const v = round3(Math.max(0, Number.isFinite(value) ? value : 0));
    const isNew = !((qtys[id] ?? 0) > 0);
    // Max counts OPTIONS chosen, as on an ordinary group.
    if (v > 0 && isNew && atMax) return;
    const next = { ...qtys };
    if (v > 0) next[id] = v;
    else delete next[id];
    onChange(next);
  }

  const totalQty = round3(chosenIds.reduce((s, id) => s + (qtys[id] ?? 0), 0));
  const totalPrice = chosenIds.reduce((s, id) => {
    const o = options.find((x) => x.id === id);
    return s + (o ? o.price_delta * (qtys[id] ?? 0) : 0);
  }, 0);

  return (
    <div className="mod-qty">
      <div className="mod-qty-tools">
        <input
          ref={searchRef}
          type="search"
          className="mod-qty-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            const first = listRef.current?.querySelector<HTMLInputElement>('input');
            first?.focus();
            first?.select();
          }}
          placeholder={t({ en: 'Search options…', ar: 'ابحث في الخيارات…' })}
          autoComplete="off"
        />
        <button
          type="button"
          className={`mod-qty-only${onlySelected ? ' on' : ''}`}
          onClick={() => setOnlySelected((v) => !v)}
        >
          {t({ en: 'Selected only', ar: 'المختار فقط' })}
        </button>
      </div>

      {chosenIds.length > 0 && (
        <div className="mod-qty-chips">
          {chosenIds.map((id) => {
            const o = options.find((x) => x.id === id)!;
            return (
              <span key={id} className="mod-qty-chip">
                {localized(o, `#${o.id}`)} × {qtys[id]}
                <button
                  type="button"
                  onClick={() => setQty(id, 0)}
                  aria-label={t({ en: 'Remove', ar: 'إزالة' })}
                >×</button>
              </span>
            );
          })}
        </div>
      )}

      <div className="mod-qty-list" ref={listRef}>
        {rows.length === 0 ? (
          <div className="mod-qty-empty">
            {t({ en: 'No matching option', ar: 'لا يوجد خيار مطابق' })}
          </div>
        ) : rows.map((o) => {
          const v = qtys[o.id] ?? 0;
          const sel = v > 0;
          const blocked = !sel && atMax;
          return (
            <div key={o.id} className={`mod-qty-row${sel ? ' sel' : ''}${blocked ? ' blocked' : ''}`}>
              <div className="mod-qty-name">
                <div>{localized(o, `#${o.id}`)}</div>
                <div className="mod-qty-unit">
                  {fmt(o.price_delta)} / {t({ en: 'unit', ar: 'وحدة' })}
                </div>
              </div>
              <div className="mod-qty-stepper">
                <button type="button" tabIndex={-1} disabled={blocked} onClick={() => setQty(o.id, v + STEP)}>+</button>
                <input
                  type="number"
                  min={0}
                  step="any"
                  inputMode="decimal"
                  placeholder="0"
                  disabled={blocked}
                  value={sel ? v : ''}
                  onChange={(e) => setQty(o.id, parseFloat(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    searchRef.current?.select();
                    searchRef.current?.focus();
                  }}
                  aria-label={localized(o, `#${o.id}`)}
                />
                <button type="button" tabIndex={-1} disabled={!sel} onClick={() => setQty(o.id, v - STEP)}>−</button>
              </div>
              <div className="mod-qty-total">{sel ? fmt(o.price_delta * v) : ''}</div>
            </div>
          );
        })}
      </div>

      <div className="mod-qty-sum">
        <span>
          {t({ en: 'Options', ar: 'عدد الخيارات' })}: <b>{chosenIds.length}</b>
          {' — '}
          {t({ en: 'Total qty', ar: 'إجمالي الكمية' })}: <b>{totalQty}</b>
        </span>
        <b>{totalPrice > 0 ? '+' : ''}{fmt(totalPrice)}</b>
      </div>
    </div>
  );
}
